import { memo } from "react";
import { LinearProgress, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { faCalendarXmark, faWarehouse } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { UNIT_LABELS } from "../../../DTOs/DrillBlast/ExplosivesDTO";
import { EXPLOSIVES_STORE_TARGET } from "../../../utilities/helpers/dashboardLinks";
import { AccessLink } from "../../AccessLink/AccessLink";
import { Pill } from "../../DrillBlast/Indicators";
import { expiresSoon, explosivesStockQuery } from "../../Hooks/useExplosivesLookups";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";

const kg = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Each magazine's explosives held against its licence, and the lots near or past their use-by date (spec 10, item 9). */
export const ExplosivesStockCard: React.FC = memo(() => {
  const { isFullScreen } = useDashboardCard();
  const { data: stock = [], isLoading, isError } = useQuery({ ...explosivesStockQuery, retry: false });

  if (isLoading) return <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col" />;

  if (isError || stock.length === 0) {
    return (
      <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
        <DashboardCardEmptyState isFullScreen={isFullScreen} icon={faWarehouse} title="No magazines"
          subtitle="There are no magazines you can see. An admin adds them on the Magazines page." />
      </Paper>
    );
  }

  return (
    <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
      <div className="animate-fade-in flex flex-col gap-2 px-3 py-3 overflow-y-auto">
        {stock.map(s => {
          const licensed = s.magazine.licensedNeqKg;
          const used = licensed ? Math.min(100, s.neqKg / licensed * 100) : null;
          const ageing = s.balances.filter(b => b.quantity > 0 && expiresSoon(b.expiresOn));
          return (
            <AccessLink key={s.magazine.id} target={EXPLOSIVES_STORE_TARGET}
              className="rounded-xl border border-theme-border bg-theme-surface px-3 py-2 flex flex-col gap-1 transition"
              linkedClassName="cursor-pointer hover:border-theme-table-heading-contrast hover:bg-theme-surface-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="body2" className="!font-bold !text-theme-text">{s.magazine.name}</Typography>
                <div className="flex flex-wrap gap-1">
                  {s.parkedMovements > 0 && <Pill tone="danger">{s.parkedMovements} waiting for an admin</Pill>}
                  {s.hasPendingStocktake && <Pill tone="warning">Stocktake to approve</Pill>}
                </div>
              </div>
              <Typography variant="caption" className="!text-theme-text-muted">
                {kg(s.neqKg)} kg NEQ{licensed ? ` of ${kg(licensed)} kg licensed` : ""}
              </Typography>
              {used != null && <LinearProgress variant="determinate" value={used} color={used > 90 ? "error" : used > 75 ? "warning" : "success"} />}
              {ageing.map(b => (
                <Typography key={`${b.blastMaterialId}-${b.lotId ?? 0}`} variant="caption" className="!text-theme-warning flex items-center gap-1">
                  <FontAwesomeIcon icon={faCalendarXmark} />
                  {b.material}{b.lotCode ? ` lot ${b.lotCode}` : ""}: {b.quantity.toLocaleString()} {UNIT_LABELS[b.unit]}
                  {b.expiresOn!.diffNow("days").days < 0 ? ` expired ${b.expiresOn!.toFormat("d MMM")}` : ` use by ${b.expiresOn!.toFormat("d MMM")}`}
                </Typography>
              ))}
            </AccessLink>
          );
        })}
      </div>
    </Paper>
  );
});
